import { useState, useRef, useCallback } from 'react'
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react'
import { useBrandKitExtraction } from '../../hooks/useBrandKitExtraction'
import { BrandKit } from '../../types/brandKit'
import { brandKitApi } from '../../lib/api/brandKits'
import { AdvertiserMultiSelect } from '../../components/advertisers/AdvertiserMultiSelect'
import { Advertiser } from '../../lib/api/advertisers'
import toast from 'react-hot-toast'

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/svg+xml',
]

const ACCEPTED_EXTENSIONS = '.pdf,.doc,.docx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp,.svg'
const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

interface ExtractBrandKitModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: (brandKit: BrandKit) => void
}

export function ExtractBrandKitModal({ isOpen, onClose, onComplete }: ExtractBrandKitModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  /**
   * Advertisers this brand kit will belong to. Same picker as the
   * manual create flow — BE role-scopes the list so admins see every
   * advertiser and regular users only see the ones they can access.
   * Required for the same reason as the manual form: without an
   * assignment the resulting kit is invisible to the BrandKitPicker
   * (which filters by advertiser_id for non-admin users).
   */
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<Advertiser[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    status,
    extractedBrandKit,
    extractedBrandData,
    error,
    startExtraction,
    reset,
  } = useBrandKitExtraction()

  const handleClose = useCallback(() => {
    if (status === 'uploading' || status === 'extracting') return
    reset()
    setSelectedFile(null)
    setName('')
    setSelectedAdvertisers([])
    setDragActive(false)
    onClose()
  }, [status, reset, onClose])

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) return 'File must be under 25 MB'
    if (!ACCEPTED_TYPES.includes(file.type) && file.type !== '') {
      const ext = file.name.split('.').pop()?.toLowerCase()
      const validExts = ACCEPTED_EXTENSIONS.split(',').map(e => e.replace('.', ''))
      if (!ext || !validExts.includes(ext)) return `Unsupported file type: .${ext || 'unknown'}`
    }
    return null
  }

  const handleFileSelect = (file: File) => {
    const err = validateFile(file)
    if (err) {
      toast.error(err)
      return
    }
    setSelectedFile(file)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }

  const handleStartExtract = async () => {
    if (!selectedFile) return
    if (selectedAdvertisers.length === 0) {
      toast.error('Please select at least one advertiser')
      return
    }
    await startExtraction({
      file: selectedFile,
      name: name.trim() || undefined,
      // Extract API expects string ids in `advertiser_ids[]` (see
      // `brandKitApi.extractBrandKit`). Passing them here means the
      // resulting kit is scoped to the right advertiser(s) from
      // creation — the BrandKitPicker in Home chat will find it
      // immediately on non-admin sessions.
      advertiser_ids: selectedAdvertisers.map((a) => String(a.id)),
    })
  }

  const handleSaveAndClose = async () => {
    if (!extractedBrandKit) return
    setIsSaving(true)
    try {
      const updated = await brandKitApi.updateBrandKit(
        extractedBrandKit.id,
        { name: extractedBrandKit.name },
        { status: 'active' }
      )
      onComplete(updated)
      toast.success('Brand kit saved successfully')
      handleClose()
    } catch {
      toast.error('Failed to save brand kit')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  const isProcessing = status === 'uploading' || status === 'extracting'

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      <div
        className="relative bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                Extract
              </div>
              <h2 className="text-2xl font-bold mt-1">Extract brand kit from file</h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Upload a brand document and we'll auto-fill the brand kit for you.
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Status: idle / file selection */}
          {(status === 'idle' || status === 'failed') && (
            <>
              {/* Advertisers (required) — same picker as the manual
                  create modal. Ordered first so scoping is the very
                  first decision, matching the manual form's tab layout. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Advertisers <span className="text-red-500">*</span>
                </label>
                <AdvertiserMultiSelect
                  selected={selectedAdvertisers}
                  onChange={setSelectedAdvertisers}
                  disabled={isSaving}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Selected advertisers will have access to this brand kit.
                </p>
              </div>

              {/* Name (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Brand Kit Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Auto-detected from file if blank"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                />
              </div>

              {/* Dropzone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
                    : selectedFile
                    ? 'border-green-400 bg-green-50 dark:bg-green-900/10'
                    : 'border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 bg-gray-50 dark:bg-gray-900/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); e.target.value = '' }}
                  className="hidden"
                />
                {selectedFile ? (
                  <>
                    <FileText className="w-8 h-8 text-green-500" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null) }}
                      className="text-xs text-gray-500 hover:text-red-500 underline"
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Drop your file here or <span className="text-primary-600">browse</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PDF, DOC, DOCX, PPT, PPTX, TXT, CSV, PNG, JPEG, GIF, WEBP, SVG (max 25 MB)
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
            </>
          )}

          {/* Status: uploading / extracting */}
          {isProcessing && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border-4 border-primary-100 dark:border-primary-900/30" />
                <Loader2 className="w-16 h-16 text-primary-600 animate-spin absolute inset-0" />
              </div>
              <div className="text-center">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  {status === 'uploading' ? 'Uploading file...' : 'Extracting brand data...'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {status === 'extracting'
                    ? 'AI is analyzing your file. This may take a moment.'
                    : 'Please wait while we upload your file.'}
                </p>
              </div>
            </div>
          )}

          {/* Status: completed */}
          {status === 'completed' && extractedBrandData && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    Extraction complete
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                    Brand data has been extracted from your file.
                  </p>
                </div>
              </div>

              {/* Preview of extracted data */}
              <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-lg max-h-64 overflow-y-auto">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Extracted Data</h4>
                {extractedBrandData.description && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{extractedBrandData.description}</p>
                  </div>
                )}
                {extractedBrandData.industry && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Industry</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{extractedBrandData.industry}</p>
                  </div>
                )}
                {extractedBrandData.target_audience && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Target Audience</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{extractedBrandData.target_audience}</p>
                  </div>
                )}
                {extractedBrandData.tagline && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tagline</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{extractedBrandData.tagline}</p>
                  </div>
                )}
                {extractedBrandData.tone && typeof extractedBrandData.tone === 'object' && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tone</span>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                      {(extractedBrandData.tone as any).voice || 'N/A'}
                      {(extractedBrandData.tone as any).style ? ` / ${(extractedBrandData.tone as any).style}` : ''}
                    </p>
                  </div>
                )}
                {extractedBrandData.colors && (
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Colors</span>
                    <div className="flex gap-2 mt-1">
                      {Object.entries(extractedBrandData.colors).map(([key, value]) => {
                        const color = Array.isArray(value) ? value[0] : value
                        if (!color || typeof color !== 'string') return null
                        return (
                          <div key={key} className="flex items-center gap-1.5">
                            <div className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600" style={{ backgroundColor: color }} />
                            <span className="text-xs text-gray-600 dark:text-gray-400">{key}: {color}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
          {status === 'completed' ? (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleSaveAndClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSaving ? 'Saving...' : 'Save Brand Kit'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleClose}
                disabled={isProcessing}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStartExtract}
                disabled={!selectedFile || isProcessing || selectedAdvertisers.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isProcessing && <Loader2 className="w-4 h-4 animate-spin" />}
                {isProcessing ? 'Processing...' : 'Extract'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
